import { Can } from '@/lib/casl'
import { useRegisterStripePaymentMethodDomainMutation } from '@/lib/services/domains/domains.query'
import { cn, getErrorMessage } from '@/lib/utils'
import { toast } from 'sonner'
import LoadingButton from '@/components/ui/loading-button'
import { Badge } from '../ui/badge'
import { DetailCell, DetailRow, DetailRows } from '@/components/patterns/detail-rows'
import { Typography } from '@/components/ui/typography'
import {
  getStripePaymentMethodDomainMethodStatuses,
  getStripePaymentMethodDomainStatusMeta,
} from './stripe-payment-method-domain-status'

function getWalletStatus(status, domainStatus) {
  if (['not_registered', 'disabled', 'not_eligible'].includes(domainStatus)) {
    return { label: 'Inactive', variant: 'muted' }
  }

  if (status === 'active') {
    return { label: 'Active', variant: 'success' }
  }

  if (status === 'inactive') {
    return { label: 'Inactive', variant: 'muted' }
  }

  return { label: 'Unavailable', variant: 'muted' }
}

function StripePaymentMethodDomainCard({ domain, className }) {
  const stripePaymentMethodDomain = domain?.stripe_payment_method_domain
  const { mutateAsync, isPending } = useRegisterStripePaymentMethodDomainMutation()
  const statusMeta = getStripePaymentMethodDomainStatusMeta(stripePaymentMethodDomain)
  const canRegister = stripePaymentMethodDomain?.can_register
  const showRegisterAction = Boolean(statusMeta.actionLabel && canRegister)
  const paymentMethodRows = getStripePaymentMethodDomainMethodStatuses(
    stripePaymentMethodDomain
  ).map((paymentMethod) => ({
    paymentMethod: paymentMethod.label,
    ...getWalletStatus(paymentMethod.status, stripePaymentMethodDomain?.status),
  }))

  const handleRegister = async () => {
    try {
      await mutateAsync(domain.id)
      toast.success('Stripe domain registration updated successfully')
    } catch (error) {
      toast.error('Stripe domain registration failed', {
        description: getErrorMessage(error),
      })
    }
  }

  return (
    <div className={cn(className)}>
      <Typography variant="h5">Payment Method Status</Typography>
      <Typography variant="supporting">{statusMeta.description}</Typography>
      <DetailRows
        headers={['Payment Method', 'Status']}
        ariaLabel="Payment method status"
        className="mt-4"
      >
        {paymentMethodRows.map((paymentMethod) => (
          <DetailRow key={paymentMethod.paymentMethod}>
            <DetailCell label="Payment Method" className="font-medium">
              {paymentMethod.paymentMethod}
            </DetailCell>
            <DetailCell label="Status">
              <Badge variant={paymentMethod.variant}>{paymentMethod.label}</Badge>
            </DetailCell>
          </DetailRow>
        ))}
        {!paymentMethodRows.length ? (
          <DetailRow>
            <DetailCell colSpan={2} className="@[34rem]/detail-rows:col-span-full">
              <Typography variant="supporting">
                No Stripe payment method statuses are available yet.
              </Typography>
            </DetailCell>
          </DetailRow>
        ) : null}
      </DetailRows>

      <Can I="edit" a="domains">
        {showRegisterAction ? (
          <LoadingButton
            size="sm"
            isPending={isPending}
            onClick={handleRegister}
            className="mt-4 self-start"
          >
            {statusMeta.actionLabel}
          </LoadingButton>
        ) : null}
      </Can>
    </div>
  )
}

export default StripePaymentMethodDomainCard
